import React, { useEffect, useState } from 'react';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import { PurchaseDocumentDataForm, PurchaseDocumentServiceManifestDataForm } from '../../../../app/models/PurchaseDocument'; 
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import { Society } from '../../../../app/models/Society';
import LazyLoading from '../../../../components/LazyLoading';
import { momentParseDate } from '../../../../helpers';
import InputButtonFile from '../../../../components/inputs/InputButtonFile';
import { Provider } from '../../../../app/models/Provider';


interface Props {
    purchaseDocument: PurchaseDocumentDataForm;
    societies: Society[];
    providers: Provider[];
    disabled?: boolean;
    setPurchaseDocument: (purchaseDocument: PurchaseDocumentDataForm) => void;
    availableStatuses: PurchaseDocumentServiceManifestDataForm[];
    errorFields?: any;
    loadingServiceManifestStatus?: boolean;
}

const PurchaseDocumentForm = ({ 
      purchaseDocument,
      setPurchaseDocument,
      availableStatuses, 
      societies,
      providers,
      disabled = false,
      loadingServiceManifestStatus,
      errorFields 
    }: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors, errors } = useHandleErrorFields();

    const [addedServiceManifests, setAddedServiceManifests] = useState<PurchaseDocumentServiceManifestDataForm[]>([]);
    const [selectedServiceManifest, setSelectedServiceManifest] = useState<PurchaseDocumentServiceManifestDataForm | null>(null);
    const [totalAmount, setTotalAmount] = useState<number>(0);

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields, setErrors]);

    /**
     * Parsea un valor monetario a número. No inventa nada: usa lo que viene (número o string).
     * - Si es número: se devuelve tal cual.
     * - Si es string formato JSON/US (punto decimal): "192101.00" → 192101
     * - Si es string formato es-CL (coma decimal, punto miles): "228.600,19" → 228600.19
     */
    const parseCurrencyToNumber = (currency: string | number): number => {
        if (typeof currency === 'number' && !Number.isNaN(currency)) {
            return currency;
        }
        if (typeof currency !== 'string') return 0;
        const sinSimbolos = currency.replace(/[^0-9.,]/g, '');
        const tieneComma = sinSimbolos.includes(',');
        const ultimoPunto = sinSimbolos.lastIndexOf('.');
        const ultimaComma = sinSimbolos.lastIndexOf(',');
        const esFormatoChileno = tieneComma && (ultimoPunto === -1 || ultimoPunto < ultimaComma);
        let normalizado: string;
        if (esFormatoChileno) {
            normalizado = sinSimbolos.replace(/\./g, '').replace(',', '.');
        } else {
            normalizado = sinSimbolos.replace(/,/g, '');
        }
        const parsed = parseFloat(normalizado);
        return Number.isNaN(parsed) ? 0 : parsed;
    };

    const formatCurrency = (amount: number, withDecimals = true) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: withDecimals ? 2 : 0,
            maximumFractionDigits: withDecimals ? 2 : 0,
        }).format(amount);
    };

    const mappedStatuses = availableStatuses.map((status) => {
        const total = parseCurrencyToNumber(status.total);
        const total_neto = parseCurrencyToNumber(status.total_neto);
        const total_tax = parseCurrencyToNumber(status.total_tax);
        return {
            id: status.id,
            name: `#${status.id} - ${status.provider_name} - Total: ${formatCurrency(total)}`,
            total,
            provider_name: status.provider_name,
            total_tax: String(total_tax),
            total_neto: String(total_neto),
            total_unit: String(total_neto),
        } as PurchaseDocumentServiceManifestDataForm;
    });

    const handleSelectChange = (name: string, value: any) => {
        const selected = mappedStatuses.find((status) => status.id === value.value);
        setSelectedServiceManifest(selected || null);
    };

    const handleChangeSelectTwoProvider = (name: string, value: number | string | null) => {
        setPurchaseDocument({
            ...purchaseDocument,
            [name]: value,
            provider_email: providers.find((provider) => provider.id == value)?.email_contact || ''
        });
    };

    const handleChangeSelectTwo = (name: string, value: number | string | null) => {
        if(name === 'society_id'){
            setErrors(
                {
                    ...errors,
                    provider_id:''
                }
            )
        }
        setPurchaseDocument({ ...purchaseDocument, [name]: value });
    };

    const handleChange = (
        e:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;

        setPurchaseDocument({ ...purchaseDocument, [name]: value });
    };


    const addServiceManifest = () => {
        if (selectedServiceManifest && !addedServiceManifests.some(p => p.id === selectedServiceManifest.id)) {
            setAddedServiceManifests([...addedServiceManifests, selectedServiceManifest]);
        }
    };

    const removeServiceManifest = (id: number) => {
        const updatedServiceManifests = addedServiceManifests.filter(payment => payment.id !== id);
        setAddedServiceManifests(updatedServiceManifests);
    };


    const handleChangeDocumentFile = (e: FileList | null, type: string) => {
        if (e) {
            setPurchaseDocument({ ...purchaseDocument, ['file']: e[0] });
        }
    };

    const handleRemoveFile = (name: string) => {
        setPurchaseDocument({ 
            ...purchaseDocument, 
            [name]: undefined 
        });
        const fileInput = document.getElementById(name) as HTMLInputElement | null;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    useEffect(() => {
        const sum = addedServiceManifests.reduce((acc, payment) => {
            return acc + parseCurrencyToNumber(payment.total);
        }, 0);
        const totalDocumento = Math.round(sum);

        const serviceManifestIds = addedServiceManifests.map(payment => payment.id);

        setTotalAmount(totalDocumento);

        setPurchaseDocument({
            ...purchaseDocument,
            total_amount: totalDocumento,
            service_manifest_ids: serviceManifestIds
        });
    }, [addedServiceManifests, setPurchaseDocument]);

    

    return (
        <div className="purchase-document-form">

            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Sociedad</label>
                <div className="col-md-9">
                    <SelectTwo
                        name="society_id"
                        id="society_id"
                        inputValue={purchaseDocument.society_id}
                        options={SelectTwoMapperOptions(societies, 'id', ['business_name'])}
                        hasError={fieldHasError('society_id') !== ''}
                        onChange={(value: any) => handleChangeSelectTwo('society_id', value.value)}
                        placeholder={'Seleccione una sociedad'}
                        onFocus={() => onFocusRemove('society_id')}
                        disabled={disabled}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('society_id')}
                    </div>
                </div>
            </div>

            
            <div className="mb-2 row">
                <label className="col-md-3 col-form-label">Proveedor</label>
                <div className="col-md-9">
                    <SelectTwo
                        name="provider_id"
                        id="provider_id"
                        inputValue={purchaseDocument.provider_id}
                        options={SelectTwoMapperOptions(providers)}
                        hasError={fieldHasError('provider_id') !== ''}
                        onChange={(value: any) =>
                            handleChangeSelectTwoProvider('provider_id', value.value)
                        }
                        placeholder={'Seleccione un proveedor'}
                        onFocus={() => onFocusRemove('provider_id')}
                        disabled={disabled}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('provider_id')}
                    </div>
                </div>
            </div>
                    

                    {!disabled && ( 
                    <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Folio Factura</label>
                    <div className="col-md-9">
                        <input
                            className={`form-control ${fieldHasError('folio')}`}
                            name="folio"
                            id="folio"
                            type="text"
                            value={purchaseDocument.folio}
                            onChange={handleChange}
                            placeholder="Ingrese el folio de la factura"
                            onFocus={() => onFocusRemove('folio')}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('folio')}
                        </div>
                    </div>
                    </div>

                    )}
                    {!disabled && ( 

                    <div className="mb-2 row">
                        <label className="col-md-3 col-form-label">Fecha de emisión</label>
                        <div className="col-md-9">
                            <input
                                type="date"
                                className="form-control"
                                id="issue_date"
                                name="issue_date"
                                value={purchaseDocument.issue_date}
                                onChange={handleChange}
                                onFocus={() => onFocusRemove('issue_date')}
                                // disabled={disabled}
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('issue_date')}
                            </div>
                        </div>
                    </div>
                    )}

                <div className="mb-2 row">
                    <label className="col-md-3 col-form-label">Factura de Compra</label>
                    <div className="col-md-9">

                        <InputButtonFile
                            name={'file'}
                            objectFile={purchaseDocument.file}
                            handleChangeDocumentFile={handleChangeDocumentFile}
                            handleRemoveFile={(value: any) => handleRemoveFile('file')}
                            onFocus={() => onFocusRemove('file')}
                            // disabled={disabled}
                        />

                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('file')}
                        </div>
                    </div>
                </div>
            {
                loadingServiceManifestStatus ?
                <LazyLoading height={300} />
                :
                <>
                    <label className="col-md-6 col-form-label">Documento de Factura</label>
                    <div className="d-flex mb-3 align-items-stretch">
                        <div className="flex-grow-1 me-2">
                            <SelectTwo
                                name="services_manifest_id"
                                id="services_manifest_id"
                                inputValue={selectedServiceManifest ? selectedServiceManifest.id : null}
                                hasError={fieldHasError('service_manifest_id') !== ''}
                                options={SelectTwoMapperOptions(mappedStatuses, 'id', ['name'])}
                                onChange={(value: any) => handleSelectChange('services_manifest_id', value)}
                                onFocus={() => onFocusRemove('service_manifest_id')}
                                placeholder="Seleccionar Documento"
                            />
                            <div className="invalid-feedback" style={{ display: 'flex' }}>
                                {fieldErrorMessage('service_manifest_id')}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={addServiceManifest}
                            disabled={!selectedServiceManifest}
                        >
                            Agregar Documento
                        </button>
                    </div>
                </>
            }
            
            <div className="table-form">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Nro Documento</th>
                            {/* <th>Providere</th> */}
                            <th>Neto</th>
                            <th>Impuesto</th>
                            <th>Total</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {addedServiceManifests.map((payment) => (
                            <tr key={payment.id}>
                                <td data-label="Nro">#{payment.id}</td>
                                {/* <td data-label="Providere">{payment.provider_name}</td> */}
                                <td data-label="Neto">{formatCurrency(parseCurrencyToNumber(payment.total_neto))}</td>
                                <td data-label="Impuesto">{formatCurrency(parseCurrencyToNumber(payment.total_tax))}</td>
                                <td data-label="Total">{formatCurrency(parseCurrencyToNumber(payment.total))}</td>
                                <td data-label="Acciones">
                                    <ButtonTableAction
                                        callbackFunction={() => removeServiceManifest(payment.id ?? -1)}
                                        classIcon={'mdi mdi-delete'}
                                        colorIcon={'text-danger'}
                                        errorMessage={'No se puede eliminar este registro.'}
                                        title={'Eliminar'}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="total mt-3 d-flex justify-content-end">
                <span className="me-2">Total:</span>
                <span>{formatCurrency(totalAmount, false)}</span>
            </div>
        </div>
    );
};

export default PurchaseDocumentForm;
